import type { IStyleParam } from '@wix/tpa-settings';
import {
  createStylesParams,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import { StyleType } from '@wix/editor-platform-ooi-metadata-resolver';

const defaultBodyMFont: NonNullable<
  IStyleParam<StyleParamType.Font>['getDefaultValue']
> = (params) =>
  wixFontParam('Body-M', {
    htmlTag: 'p',
    size: params.isMobile ? 14 : 16,
  })(params);

export type DispatchInfoStylesParams = {
  dispatchTextFont: StyleParamType.Font;
  dispatchTextColor: StyleParamType.Color;
  dispatchLinkFont: StyleParamType.Font;
  dispatchLinkColor: StyleParamType.Color;
  dispatchShowIcon: StyleParamType.Boolean;
  dispatchIconSize: StyleParamType.Number;
  dispatchIconColor: StyleParamType.Color;
};

export const dispatchInfoStylesParams =
  createStylesParams<DispatchInfoStylesParams>({
    dispatchTextFont: {
      type: StyleParamType.Font,
      getDefaultValue: defaultBodyMFont,
      metadata: {
        type: StyleType.font,
        defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
        mobileDefaultValue: { type: 'static', preset: 'Body-M', size: 14 },
        ai: {
          description:
            'Font for the dispatch info bar above the menu (shows current delivery address and estimated time)',
        },
      },
    },
    dispatchTextColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.color,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description:
            'Text color of the dispatch info bar above the menu (shows delivery address and estimated time)',
        },
      },
    },
    dispatchLinkFont: {
      type: StyleParamType.Font,
      getDefaultValue: defaultBodyMFont,
      metadata: {
        type: StyleType.font,
        defaultValue: { type: 'static', preset: 'Body-M', size: 16 },
        mobileDefaultValue: { type: 'static', preset: 'Body-M', size: 14 },
        ai: {
          description:
            'Font for the clickable change-address link inside the dispatch info bar',
        },
      },
    },
    dispatchLinkColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-8', 1),
      metadata: {
        type: StyleType.color,
        defaultValue: { type: 'static', preset: 'color-8', opacity: 1 },
        ai: {
          description:
            'Color of the clickable change-address link inside the dispatch info bar',
        },
      },
    },
    dispatchShowIcon: {
      type: StyleParamType.Boolean,
      getDefaultValue: () => true,
      metadata: {
        type: StyleType.booleanValue,
        defaultValue: { type: 'static', value: true },
        ai: {
          description:
            'Whether to show an icon (e.g. location pin or clock) next to the dispatch info bar text',
        },
      },
    },
    dispatchIconSize: {
      type: StyleParamType.Number,
      getDefaultValue: ({ isMobile }) => (isMobile ? 16 : 24),
      metadata: {
        type: StyleType.number,
        defaultValue: { type: 'static', value: 24 },
        mobileDefaultValue: { type: 'static', value: 16 },
        ai: {
          description:
            'Size (px) of the icon shown next to the dispatch info bar text',
        },
      },
    },
    dispatchIconColor: {
      type: StyleParamType.Color,
      getDefaultValue: wixColorParam('color-5', 1),
      metadata: {
        type: StyleType.color,
        defaultValue: { type: 'static', preset: 'color-5', opacity: 1 },
        ai: {
          description:
            'Color of the icon shown next to the dispatch info bar text',
        },
      },
    },
  });
